import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ISsoConfig, IApiResponse } from '@skystone/shared';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoConfigLoading, setSsoConfigLoading] = useState(true);
  const { login, ssoExchange } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;

  useEffect(() => {
    const searchParams = new URLSearchParams(globalThis.location.search);

    // Handle SSO token from callback
    const ssoToken = searchParams.get('sso_token');
    if (ssoToken) {
      ssoExchange(ssoToken)
        .then(() => {
          globalThis.history.replaceState({}, '', '/');
          navigate('/', { replace: true });
        })
        .catch(() => {
          globalThis.history.replaceState({}, '', '/login');
          setError('SSO authentication failed. Please try again.');
        });
      return; // Don't fetch config if we're navigating away
    }

    // Handle SSO error from callback
    const ssoError = searchParams.get('sso_error');
    if (ssoError) {
      const errorMessages: Record<string, string> = {
        email_conflict: 'An account with this email already exists. Please log in with your password.',
        not_configured: 'SSO is not currently available. Please contact your administrator.',
        invalid_state: 'SSO authentication failed. Please try again.',
        server_error: 'SSO authentication failed due to a server error. Please try again later.',
      };
      setError(errorMessages[ssoError] || 'SSO authentication failed. Please try again.');
      globalThis.history.replaceState({}, '', '/login');
    }

    // Fetch SSO config
    api.get<IApiResponse<ISsoConfig>>('/auth/sso/config')
      .then(response => {
        setSsoEnabled(response.data.enabled);
      })
      .catch(() => {
        // SSO config fetch failed — keep button hidden
      })
      .finally(() => {
        setSsoConfigLoading(false);
      });
  }, [navigate, ssoExchange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      await login({ email, password });
      navigate('/', { replace: true });
    } catch {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-foreground">Skystone</h1>
          <p className="text-sm text-muted-foreground mt-2">Sign in to your account</p>
        </div>

        {successMessage && (
          <div className="rounded-md bg-success-50 border border-success-200 p-3 mb-4">
            <p className="text-sm text-success-700">{successMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-md border bg-background text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                'placeholder:text-muted-foreground',
                error ? 'border-error-500' : 'border-border'
              )}
              placeholder="your.email@example.com"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-md border bg-background text-foreground',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                'placeholder:text-muted-foreground',
                error ? 'border-error-500' : 'border-border'
              )}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md bg-error-50 border border-error-200 p-3">
              <p className="text-sm text-error-700">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            isLoading={isLoading}
          >
            Sign in
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Contact an administrator to get an account.
            </p>
          </div>
        </form>

        {ssoEnabled && !ssoConfigLoading && (
          <>
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            {/* SSO Button */}
            <a
              href="/api/auth/sso/login"
              className={cn(
                'w-full inline-flex items-center justify-center rounded-md border border-border',
                'bg-background px-6 h-12 text-sm font-medium text-foreground',
                'hover:bg-accent hover:text-accent-foreground transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
              )}
            >
              Login with Odevo SSO
            </a>
          </>
        )}
      </div>
    </div>
  );
}
